import React from 'react'

import type { MascotFurType } from '../mascot-types'
import { coatFill, flatCoatFill, type MascotRegion } from './mascot-svg-shared'

/**
 * Coat length, drawn as a fringe around the head.
 *
 * Placed around the species' own head region rather than at fixed
 * coordinates, so the same fur works on a round cat face and a long horse
 * one. Draw it BEFORE the head (and before the ears) — the fringe is meant to
 * peek out from behind the silhouette, and the skull painting over its roots
 * is what stops it looking like a collar sitting on top of the face.
 */

const CURL_COUNT = 24
const TUFT_COUNT = 18
/** How far each lock is pulled from "straight out" toward "straight down". */
const DROOP = 0.4
/** Locks above this point on the head are dropped — see the crown note. */
const CROWN_CUTOFF = -0.55

/**
 * Curls are a fraction of a turn out of phase with each other so the ring
 * doesn't read as a machine-stamped row of identical bumps.
 */
const CURL_JITTER = [0, 0.14, -0.09, 0.06, -0.16, 0.11, -0.04, 0.18]

export default function MascotFur({
    furType,
    region,
    flat = false,
}: {
    furType: MascotFurType
    region: MascotRegion
    /** Match the species' rendering style — see `flatCoatFill`. */
    flat?: boolean
}) {
    if (furType === 'short') return null

    const { cx, cy, rx, ry } = region
    const fill = flat ? flatCoatFill : coatFill

    if (furType === 'curly') {
        const curls: React.ReactNode[] = []
        for (let i = 0; i < CURL_COUNT; i += 1) {
            const jitter = CURL_JITTER[i % CURL_JITTER.length]
            const t = ((i + jitter) / CURL_COUNT) * Math.PI * 2
            // Sunk slightly inside the head so each curl overlaps its
            // neighbours and the ring reads as one scalloped edge.
            const x = cx + Math.cos(t) * rx * 0.94
            const y = cy + Math.sin(t) * ry * 0.94
            const r = rx * (0.14 + (i % 3) * 0.012)
            curls.push(<circle key={i} cx={x} cy={y} r={r} {...fill} />)
        }
        return <g>{curls}</g>
    }

    // Long: soft locks hanging outward and down.
    const locks: React.ReactNode[] = []
    for (let i = 0; i < TUFT_COUNT; i += 1) {
        const t = (i / TUFT_COUNT) * Math.PI * 2
        const cos = Math.cos(t)
        const sin = Math.sin(t)
        // Skip the crown. Hair there lies flat against the skull, and locks
        // standing up off the top of the head read as spikes.
        if (sin < CROWN_CUTOFF) continue

        const x = cx + cos * rx * 0.9
        const y = cy + sin * ry * 0.9

        // Bias each lock toward straight down so the fringe hangs instead of
        // radiating. Angles on the upper left come back from atan2 as large
        // negatives, which would blend the wrong way round the circle, so
        // they're lifted into the same turn as everything else first.
        let radial = (Math.atan2(sin * ry, cos * rx) * 180) / Math.PI
        if (radial < -90) radial += 360
        const angle = radial * (1 - DROOP) + 90 * DROOP

        const length = rx * 0.28 * (1 + Math.max(0, sin) * 0.55)
        const halfWidth = rx * 0.155
        // Rounded tip rather than a point — locks taper, they don't spike.
        const d = [
            `M0 ${-halfWidth}`,
            `C ${length * 0.5} ${-halfWidth} ${length * 0.82} ${-halfWidth * 0.6} ${length} 0`,
            `C ${length * 0.82} ${halfWidth * 0.6} ${length * 0.5} ${halfWidth} 0 ${halfWidth}`,
            'Z',
        ].join(' ')

        locks.push(
            <path
                key={i}
                d={d}
                transform={`translate(${x} ${y}) rotate(${angle})`}
                {...fill}
            />,
        )
    }
    return <g>{locks}</g>
}
